import React, { useState, useRef } from "react";
import { compressImageDataUrl } from "../shared/PhotoUploader";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// Iter-74 — Dashboard Hero Images (Super Admin only)
// ============================================================
// Seven per-day image slots (sunday..saturday). Each slot stores:
//   { image: <base64 dataURL>, zoom: 100..200, position: "…", overlay: 0..80 }
// The dashboard reads `dashboardHeroImages[<todayKey>]` at render time
// and cycles automatically based on the user's local browser day.
// If no image is uploaded for the current day the hero falls back to
// the accent gradient (existing behaviour).
// ============================================================

const DAYS = [
  { key: "sunday",    label: "Sunday" },
  { key: "monday",    label: "Monday" },
  { key: "tuesday",   label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday",  label: "Thursday" },
  { key: "friday",    label: "Friday" },
  { key: "saturday",  label: "Saturday" },
];

const POSITIONS = [
  ["top left", "top", "top right"],
  ["left", "center", "right"],
  ["bottom left", "bottom", "bottom right"],
];

const DEFAULT_SLOT = { image: "", zoom: 100, position: "center", overlay: 40 };

export function DashboardHeroImagesSection({ settings, update }) {
  const heroImages = settings?.dashboardHeroImages || {};
  const [openDay, setOpenDay] = useState("sunday");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const slot = { ...DEFAULT_SLOT, ...(heroImages[openDay] || {}) };

  const patchSlot = async (dayKey, patch) => {
    const merged = {
      ...heroImages,
      [dayKey]: { ...DEFAULT_SLOT, ...(heroImages[dayKey] || {}), ...patch },
    };
    await update({ dashboardHeroImages: merged });
  };

  const clearSlot = async (dayKey) => {
    const next = { ...heroImages };
    delete next[dayKey];
    await update({ dashboardHeroImages: next });
  };

  // Copy the current slot's image + zoom + position + overlay to every
  // day of the week. Convenience for churches that want a consistent hero.
  const copyToAllDays = async () => {
    if (!slot.image) return;
    const payload = {
      image: slot.image,
      zoom: slot.zoom,
      position: slot.position,
      overlay: slot.overlay,
    };
    const merged = {};
    for (const d of DAYS) {
      merged[d.key] = { ...DEFAULT_SLOT, ...payload };
    }
    await update({ dashboardHeroImages: merged });
    setToast("Applied to all 7 days.");
    setTimeout(() => setToast(""), 2600);
  };

  return (
    <section data-testid="hero-images-section" style={{ marginTop: 32 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="picture" size={18} /> Dashboard Hero Images
      </div>
      <div className="text-card-secondary" style={{ marginBottom: 12 }}>
        Upload one image per weekday. The dashboard hero cycles automatically based on the viewer&apos;s local day. If a day has no image, the hero falls back to the accent gradient.
      </div>

      {/* Day picker chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {DAYS.map((d) => {
          const has = !!(heroImages[d.key]?.image);
          const active = openDay === d.key;
          return (
            <button
              key={d.key}
              type="button"
              data-testid={`hero-day-${d.key}`}
              onClick={() => setOpenDay(d.key)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? "var(--kc-accent)" : "var(--kc-border)"}`,
                background: active ? "var(--kc-accent)" : "var(--kc-elevated)",
                color: active ? "var(--kc-on-accent)" : "var(--kc-text)",
                fontFamily: "'Montserrat',sans-serif",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {d.label}
              {has && <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "var(--kc-on-accent)" : "var(--kc-accent)" }} />}
            </button>
          );
        })}
      </div>

      <DaySlotEditor
        key={openDay}
        dayKey={openDay}
        slot={slot}
        busy={busy}
        setBusy={setBusy}
        onPatch={(patch) => patchSlot(openDay, patch)}
        onClear={() => clearSlot(openDay)}
        onCopyToAll={copyToAllDays}
      />
      {toast && (
        <div
          data-testid="hero-copy-all-toast"
          role="status"
          style={{
            marginTop: 10,
            padding: "8px 14px",
            borderRadius: "var(--kc-radius-sm)",
            background: "var(--kc-green-bg)",
            color: "var(--kc-green)",
            fontFamily: "'Inter',sans-serif",
            fontSize: 12,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <CustomIcon name="check" size={12} /> {toast}
        </div>
      )}
    </section>
  );
}

function DaySlotEditor({ dayKey, slot, busy, setBusy, onPatch, onClear, onCopyToAll }) {
  const fileRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      // Iter-74 spec: max 300 KB at 1200 px wide.
      const compressed = await compressImageDataUrl(dataUrl, { maxDimension: 1200, maxBytes: 300 * 1024 });
      await onPatch({ image: compressed });
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Upload failed: ${err.message || err}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={`hero-slot-${dayKey}`}
      style={{
        background: "var(--kc-surface)",
        border: "1px solid var(--kc-border)",
        borderRadius: 12,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Upload row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onPick}
          style={{ display: "none" }}
          data-testid={`hero-upload-input-${dayKey}`}
        />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`hero-upload-btn-${dayKey}`}
        >
          <CustomIcon name="upload" size={12} style={{ marginRight: 6 }} />
          {slot.image ? "Replace image" : "Upload image"}
        </button>
        {slot.image && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={onClear}
            disabled={busy}
            data-testid={`hero-remove-btn-${dayKey}`}
            style={{ color: "#dc2626" }}
          >
            Remove
          </button>
        )}
        {busy && <span className="text-meta">Compressing…</span>}
      </div>

      {/* Only show controls once an image is present */}
      {slot.image && (
        <>
          {/* Zoom slider */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="text-card-secondary">
              Zoom <strong>{slot.zoom}%</strong>
            </span>
            <input
              type="range"
              min={100}
              max={200}
              step={5}
              value={slot.zoom}
              onChange={(e) => onPatch({ zoom: Number(e.target.value) })}
              data-testid={`hero-zoom-${dayKey}`}
            />
          </label>

          {/* Position grid */}
          <div>
            <div className="text-card-secondary" style={{ marginBottom: 6 }}>Crop position</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 32px)", gridTemplateRows: "repeat(3, 32px)", gap: 4 }}>
              {POSITIONS.flat().map((pos) => {
                const active = slot.position === pos;
                return (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => onPatch({ position: pos })}
                    aria-label={pos}
                    title={pos}
                    data-testid={`hero-pos-${dayKey}-${pos.replace(/\s+/g, "-")}`}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: `1px solid ${active ? "var(--kc-accent)" : "var(--kc-border)"}`,
                      background: active ? "var(--kc-accent)" : "var(--kc-elevated)",
                      cursor: "pointer",
                    }}
                  />
                );
              })}
            </div>
            {/* Copy-to-all-days convenience — Iter 77 (Phase 5G). */}
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn-secondary btn-sm"
                data-testid={`hero-copy-all-${dayKey}`}
                onClick={onCopyToAll}
                disabled={busy || !slot.image}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <CustomIcon name="recurring" size={12} />
                Copy to all days
              </button>
            </div>
          </div>

          {/* Overlay slider */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="text-card-secondary">
              Overlay darkness <strong>{slot.overlay}%</strong>
            </span>
            <input
              type="range"
              min={0}
              max={80}
              step={5}
              value={slot.overlay}
              onChange={(e) => onPatch({ overlay: Number(e.target.value) })}
              data-testid={`hero-overlay-${dayKey}`}
            />
          </label>

          {/* Live preview */}
          <div>
            <div className="text-card-secondary" style={{ marginBottom: 6 }}>Live preview</div>
            <HeroImagePreview slot={slot} />
          </div>
        </>
      )}
    </div>
  );
}

// Compact preview that mirrors how the Dashboard hero renders the image.
function HeroImagePreview({ slot }) {
  return (
    <div
      data-testid="hero-preview"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 360,
        height: 120,
        borderRadius: "var(--kc-radius, 14px)",
        overflow: "hidden",
        background: "var(--kc-accent-grad)",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${slot.image})`,
          backgroundSize: "cover",
          backgroundPosition: slot.position || "center",
          transform: `scale(${(slot.zoom || 100) / 100})`,
          transformOrigin: slot.position || "center",
          transition: "transform 0.2s ease, background-position 0.2s ease",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: `rgba(0,0,0,${(slot.overlay || 0) / 100})`,
        }}
      />
      <div
        style={{
          position: "relative",
          color: "var(--kc-on-accent)",
          padding: 14,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          fontFamily: "'Cinzel',serif",
          letterSpacing: "0.04em",
          fontSize: 16,
          fontWeight: 700,
          textShadow: (slot.overlay || 0) < 20 ? "0 1px 4px rgba(0,0,0,0.5)" : "none",
        }}
      >
        Good morning, Friend
        <div
          style={{
            marginTop: 4,
            fontFamily: "'Inter',sans-serif",
            fontSize: 11,
            fontWeight: 500,
            opacity: 0.9,
          }}
        >
          Live preview
        </div>
      </div>
    </div>
  );
}

export default DashboardHeroImagesSection;
